import React from 'react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import Icon from '../../../components/AppIcon';

const TrackingTimeline = ({ shipment, getCarrierName }) => {
  const getEventIcon = (eventType) => {
    const icons = {
      order_placed: 'ShoppingCart',
      order_confirmed: 'CheckCircle',
      payment_received: 'CreditCard',
      processing_started: 'Package',
      ready_for_pickup: 'Box',
      picked_up: 'Truck',
      in_transit: 'Navigation',
      arrived_at_hub: 'Building',
      out_for_delivery: 'MapPin',
      delivery_attempted: 'AlertCircle',
      delivered: 'CheckCircle',
      delivery_failed: 'XCircle',
      returned_to_sender: 'RotateCcw',
      cancelled: 'X'
    };
    return icons?.[eventType] || 'Circle';
  };

  const getEventColor = (eventType) => {
    const colors = {
      order_placed: 'text-blue-500',
      order_confirmed: 'text-green-500',
      payment_received: 'text-emerald-500',
      processing_started: 'text-purple-500',
      ready_for_pickup: 'text-indigo-500',
      picked_up: 'text-orange-500',
      in_transit: 'text-cyan-500',
      arrived_at_hub: 'text-teal-500',
      out_for_delivery: 'text-amber-500',
      delivery_attempted: 'text-yellow-500',
      delivered: 'text-green-600',
      delivery_failed: 'text-red-500',
      returned_to_sender: 'text-gray-500',
      cancelled: 'text-red-600'
    };
    return colors?.[eventType] || 'text-gray-400';
  };

  return (
    <div className="space-y-6">
      {/* Shipment Info */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Kargo Firması</span>
          <span className="font-medium">{getCarrierName(shipment?.carrier)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Takip Numarası</span>
          <span className="font-mono font-medium">{shipment?.trackingNumber}</span>
        </div>
        {shipment?.estimatedDeliveryDate && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Tahmini Teslimat</span>
            <span className="font-medium">
              {format(new Date(shipment?.estimatedDeliveryDate), 'dd MMMM yyyy', { locale: tr })}
            </span>
          </div>
        )}
        {shipment?.currentLocation && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Mevcut Konum</span>
            <span className="font-medium">{shipment?.currentLocation}</span>
          </div>
        )}
      </div>

      {/* Tracking URL */}
      {shipment?.carrierTrackingUrl && (
        <a
          href={shipment?.carrierTrackingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Icon name="ExternalLink" size={18} />
          <span>Kargo Firması Sitesinde Takip Et</span>
        </a>
      )}

      {/* Timeline */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground mb-4">Gönderi Geçmişi</h3>
        {shipment?.trackingEvents?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Clock" size={32} className="mx-auto mb-2" />
            <p>Henüz takip bilgisi bulunmuyor</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
            
            {/* Timeline Events */}
            <div className="space-y-6">
              {shipment?.trackingEvents
                ?.sort((a, b) => new Date(b?.eventTimestamp) - new Date(a?.eventTimestamp))
                ?.map((event, index) => (
                  <div key={event?.id} className="relative flex gap-4">
                    {/* Icon */}
                    <div className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-white border-2 flex items-center justify-center ${
                      index === 0 ? 'border-primary' : 'border-gray-300'
                    }`}>
                      <Icon 
                        name={getEventIcon(event?.eventType)} 
                        size={16} 
                        className={index === 0 ? 'text-primary' : getEventColor(event?.eventType)}
                      />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 pb-6">
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-foreground">
                            {event?.eventDescription}
                          </h4>
                          {index === 0 && (
                            <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                              Son Durum
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Icon name="Clock" size={14} />
                            <span>
                              {format(new Date(event?.eventTimestamp), 'dd MMM yyyy, HH:mm', { locale: tr })}
                            </span>
                          </div>
                          {event?.location && (
                            <div className="flex items-center gap-1">
                              <Icon name="MapPin" size={14} />
                              <span>{event?.location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackingTimeline;